import { SelectLoadingIndicator } from "../../shared/UxbiSpinner";
import { useCallback, useEffect, useMemo, useState, useId } from "react";
import { Form } from "react-bootstrap";
import { AsyncPaginate } from "react-select-async-paginate";

const PAGE_SIZE = 15;

const extractCollection = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.records)) return response.records;
  if (Array.isArray(response?.items)) return response.items;
  return [];
};

const extractRecord = (response) =>
  response?.data?.data || response?.data || response?.record || response;

const getLastPage = (response, page, itemCount) => {
  const pagination =
    response?.data && !Array.isArray(response.data) ? response.data : response;
  const lastPage = Number(pagination?.last_page || pagination?.lastPage || 0);
  if (lastPage) return lastPage;
  const total = Number(pagination?.total || response?.total || 0);
  if (total) return Math.max(1, Math.ceil(total / PAGE_SIZE));
  return itemCount < PAGE_SIZE ? page : page + 1;
};

export default function RemoteSelect({
  name,
  label,
  value,
  onChange,
  loadOptions,
  loadById,
  getOptionLabel,
  error,
  disabled = false,
  required = false,
  placeholder = "Selecciona una opción",
  noOptionsMessage = "No se encontraron resultados",
}) {
  const controlId = useId();
  const [selectedOption, setSelectedOption] = useState(null);

  useEffect(() => {
    let active = true;

    if (!value) {
      setSelectedOption(null);
      return () => {
        active = false;
      };
    }

    if (String(selectedOption?.id) === String(value))
      return () => {
        active = false;
      };

    loadById(value)
      .then((response) => {
        const record = extractRecord(response);
        if (active && record?.id) setSelectedOption(record);
      })
      .catch(() => {
        if (active)
          setSelectedOption({
            id: value,
            nombre: `Registro #${value}`,
            descripcion: `Registro #${value}`,
          });
      });

    return () => {
      active = false;
    };
  }, [loadById, selectedOption?.id, value]);

  const loadPage = useCallback(
    async (search, _loadedOptions, additional) => {
      const page = additional?.page || 1;
      const response = await loadOptions({ search, page, limit: PAGE_SIZE });
      const records = extractCollection(response);
      const lastPage = getLastPage(response, page, records.length);

      return {
        options: records,
        hasMore: page < lastPage,
        additional: { page: page + 1 },
      };
    },
    [loadOptions],
  );

  const selectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: 42,
        borderColor: error
          ? "#dc3545"
          : state.isFocused
            ? "#86b7fe"
            : "#dee2e6",
        boxShadow: state.isFocused
          ? `0 0 0 .25rem ${
              error ? "rgba(220,53,69,.25)" : "rgba(13,110,253,.25)"
            }`
          : "none",
        "&:hover": {
          borderColor: error
            ? "#dc3545"
            : state.isFocused
              ? "#86b7fe"
              : "#adb5bd",
        },
      }),
      menuPortal: (base) => ({ ...base, zIndex: 1060 }),
    }),
    [error],
  );

  const handleChange = (option) => {
    setSelectedOption(option);
    onChange(name, option?.id || "");
  };

  return (
    <Form.Group controlId={`client-${name}-${controlId}`}>
      <Form.Label>
        {label}
        {required && " *"}
      </Form.Label>
      <AsyncPaginate
        inputId={`client-${name}-${controlId}`}
        components={{ LoadingIndicator: SelectLoadingIndicator }}
        loadingMessage={() => (
          <span className="select-loading-message">
            <SelectLoadingIndicator /> Cargando…
          </span>
        )}
        classNamePrefix="remote-select"
        value={selectedOption}
        loadOptions={loadPage}
        onChange={handleChange}
        getOptionLabel={getOptionLabel}
        getOptionValue={(option) => String(option.id)}
        additional={{ page: 1 }}
        debounceTimeout={350}
        isClearable
        isDisabled={disabled}
        placeholder={placeholder}
        noOptionsMessage={() => noOptionsMessage}
        menuPortalTarget={document.body}
        styles={selectStyles}
      />
      {error && <div className="invalid-feedback d-block">{error}</div>}
    </Form.Group>
  );
}
